/**
 * LangmuirCirculationPage — Langmuir circulation correction
 *
 * Thorpe 2004; Plueddemann & Weller 1999. Wind-driven helical convergence
 * cells that align with the wind direction. Cell wavelength ≈ 4.8 × U10
 * (Leibovich 1983). Modules placed in convergence zones get drawn into
 * a downwelling streak; modules in divergence zones receive upwelling.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Wind, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, onPersistChange } from '@/lib/sessionPersistence';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

const ARRAY_KEY = 'rb_module_array_v1';
interface ArrayCfg { moduleCount: number; cols: number; gapXM: number; gapYM: number; }
const ARR_DEFAULTS: ArrayCfg = { moduleCount: 6, cols: 2, gapXM: 2.0, gapYM: 2.0 };

const FRAME_SIZE_M = 2.0;

const RESERVOIR_POLY: [number, number][] = [
  [85,18],[95,15],[112,14],[128,18],[142,24],[155,34],[163,48],[168,65],[170,82],
  [169,100],[166,118],[168,136],[170,155],[169,173],[167,192],[162,210],[156,228],
  [148,245],[138,262],[127,277],[116,290],[108,302],[102,315],[98,330],[94,345],
  [91,360],[89,375],[88,390],[87,403],[87,415],[85,432],[82,420],[79,408],[76,396],
  [73,383],[70,368],[67,352],[60,320],[56,303],[51,287],[47,270],[42,253],
  [39,236],[37,218],[35,200],[34,182],[35,165],[38,148],[38,130],[36,112],[37,94],
  [40,77],[46,62],[52,48],[60,35],[72,25],
];

function designedPositions(a: ArrayCfg): { x: number; y: number; idx: number }[] {
  const cols = Math.max(1, Math.min(6, a.cols));
  const rows = Math.ceil(a.moduleCount / cols);
  const pitchX = FRAME_SIZE_M + a.gapXM;
  const pitchY = FRAME_SIZE_M + a.gapYM;
  const out: { x: number; y: number; idx: number }[] = [];
  const totalW = (cols - 1) * pitchX, totalH = (rows - 1) * pitchY;
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
    if (r * cols + c >= a.moduleCount) break;
    out.push({ x: 95 + (c * pitchX - totalW / 2), y: 200 + (r * pitchY - totalH / 2), idx: r * cols + c });
  }
  return out;
}

export default function LangmuirCirculationPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('langmuir');
  const [array, setArray] = useState<ArrayCfg>(() => ({ ...ARR_DEFAULTS, ...loadPersisted<ArrayCfg>(ARRAY_KEY, ARR_DEFAULTS) }));
  const [windSpeed, setWind] = useState(4.0);
  const [windDir, setDir] = useState(240);

  useEffect(() => {
    const unsub = onPersistChange(k => {
      if (k === ARRAY_KEY) setArray({ ...ARR_DEFAULTS, ...loadPersisted<ArrayCfg>(ARRAY_KEY, ARR_DEFAULTS) });
    });
    return () => unsub();
  }, []);

  const modules = useMemo(() => designedPositions(array), [array]);

  const result = useMemo(() => {
    // Leibovich 1983: cell wavelength L_c ≈ 4.8 × U10 (in metres for U10 in m/s)
    const lambda = 4.8 * windSpeed;
    // Cells propagate in the wind direction. Calculate position of each module within
    // the perpendicular sinusoidal cell pattern.
    const windRad = (windDir - 180) * Math.PI / 180;
    const perpRad = windRad + Math.PI / 2;
    // For each module: compute distance along the perpendicular axis (relative to a reference)
    const ux = Math.sin(perpRad);
    const uy = -Math.cos(perpRad);
    const refX = 95, refY = 200;
    return modules.map(m => {
      const distAlongPerp = (m.x - refX) * ux + (m.y - refY) * uy;
      // Sinusoidal: convergence at sin = +1, divergence at sin = -1
      const phase = (2 * Math.PI * distAlongPerp) / Math.max(0.1, lambda);
      const lc_factor = Math.sin(phase);
      // Efficiency factor: divergence (upwelling) helps DO mixing, convergence hurts (downwelling
      // pulls plume back down). Range 0.7 – 1.3.
      const efficiency = 1 - 0.3 * lc_factor;
      const verdict: 'good' | 'neutral' | 'poor' = lc_factor < -0.3 ? 'good' : lc_factor > 0.3 ? 'poor' : 'neutral';
      return { ...m, lc_factor, efficiency, verdict };
    });
  }, [modules, windSpeed, windDir]);

  const lambda = 4.8 * windSpeed;
  const meanEff = result.length ? result.reduce((s, r) => s + r.efficiency, 0) / result.length : 1;

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Wind}
        title="Module — Langmuir Circulation Correction"
        subtitle="Thorpe 2004. Wind-driven helical cells; per-module efficiency depends on position in cell"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${
              enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'
            }`}>
              <input type="checkbox" checked={enabled} onChange={() => toggle('langmuir')} className="accent-emerald-500" />
              Module {enabled ? 'ENABLED' : 'DISABLED'}
            </label>
            <button onClick={() => navigate('/modules')}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Modules
            </button>
          </div>
        }
      />

      <ModuleConflictBanner moduleId="langmuir" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Wind speed</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{windSpeed.toFixed(1)} m/s</div>
        </div>
        <div className="rounded-xl border-2 border-sky-700 bg-sky-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-sky-300">Cell wavelength λ</div>
          <div className="text-2xl font-bold text-sky-200 mt-1">{lambda.toFixed(1)} m</div>
          <div className="text-xs text-slate-500 mt-1">Leibovich 1983: 4.8 × U10</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Mean efficiency</div>
          <div className={`text-2xl font-bold mt-1 ${meanEff > 1.05 ? 'text-emerald-300' : meanEff < 0.95 ? 'text-red-300' : 'text-slate-100'}`}>
            {(meanEff * 100).toFixed(0)}%
          </div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Modules in</div>
          <div className="text-xs text-slate-100 mt-1">
            <span className="text-emerald-300">{result.filter(r => r.verdict === 'good').length} ⇧</span> /{' '}
            <span className="text-slate-300">{result.filter(r => r.verdict === 'neutral').length} ─</span> /{' '}
            <span className="text-red-300">{result.filter(r => r.verdict === 'poor').length} ⇩</span>
          </div>
          <div className="text-[10px] text-slate-500 mt-1">upwell / neutral / downwell</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div>
          <label className="text-xs text-slate-400 block mb-1">Wind speed U10 (m/s)</label>
          <input type="range" min={0.5} max={12} step={0.5} value={windSpeed}
            onChange={e => setWind(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-xs text-slate-400 block mb-1">Wind direction (° from N)</label>
          <input type="range" min={0} max={359} step={5} value={windDir}
            onChange={e => setDir(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Langmuir cells & module positions</div>
        <svg viewBox="20 0 170 450" className="w-full max-w-md mx-auto h-auto bg-slate-950 rounded">
          <polygon points={RESERVOIR_POLY.map(p => p.join(',')).join(' ')}
            fill="rgba(56, 189, 248, 0.06)" stroke="#38bdf8" strokeWidth={1} />
          {/* Langmuir cell bands — perpendicular to wind */}
          {(() => {
            const windRad = (windDir - 180) * Math.PI / 180;
            const stripes = [];
            // draw N stripes aligned with wind, each lambda wide
            for (let s = -150; s < 150; s += lambda / 2) {
              const phase = ((s / lambda) * 2 * Math.PI);
              const fillVal = Math.sin(phase);
              const colour = fillVal > 0.3 ? 'rgba(220, 38, 38, 0.10)' :
                             fillVal < -0.3 ? 'rgba(34, 197, 94, 0.10)' :
                             'rgba(100, 116, 139, 0.05)';
              // Translate origin (95, 200) along perpendicular by s
              const cx = 95 + Math.sin(windRad + Math.PI / 2) * s;
              const cy = 200 - Math.cos(windRad + Math.PI / 2) * s;
              // Wide strip along wind direction
              const len = 220, halfW = lambda / 4;
              const dx = Math.sin(windRad) * len, dy = -Math.cos(windRad) * len;
              const px = Math.sin(windRad + Math.PI / 2) * halfW, py = -Math.cos(windRad + Math.PI / 2) * halfW;
              const pts = [
                `${cx - dx + px},${cy - dy + py}`,
                `${cx + dx + px},${cy + dy + py}`,
                `${cx + dx - px},${cy + dy - py}`,
                `${cx - dx - px},${cy - dy - py}`,
              ].join(' ');
              stripes.push(<polygon key={s} points={pts} fill={colour} />);
            }
            return stripes;
          })()}
          {/* Wind arrow */}
          {(() => {
            const wx = (windDir - 180) * Math.PI / 180;
            const cx0 = 150, cy0 = 30, len = 22;
            return (
              <line x1={cx0} y1={cy0} x2={cx0 + Math.sin(wx) * len} y2={cy0 - Math.cos(wx) * len}
                stroke="#a78bfa" strokeWidth={2.5} markerEnd="url(#lc-arr)" />
            );
          })()}
          <defs>
            <marker id="lc-arr" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={4} markerHeight={4} orient="auto-start-reverse">
              <path d="M0,0 L10,5 L0,10 z" fill="#a78bfa" />
            </marker>
          </defs>
          {/* Modules colour-coded by Langmuir efficiency */}
          {result.map((m, i) => {
            const colour = m.verdict === 'good' ? '#22c55e' : m.verdict === 'poor' ? '#ef4444' : '#fbbf24';
            return (
              <g key={i}>
                <circle cx={m.x} cy={m.y} r={4} fill={colour} stroke="#0a1628" strokeWidth={0.7} />
                <text x={m.x + 5} y={m.y + 2} fontSize={3.5} fill="#fff">M{i + 1}</text>
                <text x={m.x + 5} y={m.y + 5.5} fontSize={3} fill={colour}>{(m.efficiency * 100).toFixed(0)}%</text>
              </g>
            );
          })}
        </svg>
        <div className="text-[10px] text-slate-500 mt-1 text-center">
          Red stripes = surface convergence (downwelling — bad for plume rise). Green stripes = divergence
          (upwelling — good for plume rise). Modules coloured by their Langmuir efficiency multiplier.
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> Methodology
        </div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p>
            Wind-driven Langmuir cells are pairs of counter-rotating helical vortices aligned with the
            wind direction. Surface convergence zones (downwelling streaks) appear at half the cell
            wavelength λ ≈ 4.8 × U10. Modules placed in divergence zones (upwelling) get a boost to
            plume surface arrival; modules in convergence zones see partial cancellation as the
            downwelling drags the plume back down.
          </p>
          <p>
            <strong className="text-emerald-300">When enabled:</strong> the Plume Coverage Map could colour
            each module by its Langmuir efficiency (in a future revision), and the cost-per-mg-O₂
            optimiser uses the efficiency multiplier to penalise modules placed in convergence streaks.
            Current mean efficiency across the array is <strong>{(meanEff * 100).toFixed(0) }%</strong>.
          </p>
          <p>
            <strong className="text-amber-300">Engineering implication:</strong> if more than 50 % of
            modules fall in convergence streaks for the prevailing wind, consider rotating the array
            grid by 30–45° relative to the wind axis to break the alignment.
          </p>
          <p className="italic text-slate-500">References: Thorpe 2004; Plueddemann & Weller 1999; Leibovich 1983.</p>
        </div>
      </div>
    </div>
  );
}
